<template>
  <v-form
    ref="form"
    v-model="isValidationOk"
    lazy-validation>
    <v-container
      v-if="tankGroups && groupMembers"
      fill-height
      fluid
      grid-list-xl>
      <v-layout
        wrap>
        <v-flex
          xs6
          md2>
          <v-switch
            v-model="tank.enabled"
            :label="`${ $t('Enabled') }`"
            :disabled="features.weightsMeasuresLock"
            class="dover-input text-xs-center" />
        </v-flex>
        <v-flex
          xs6
          md4>
          <v-text-field
            v-model.trim="tank.tankDescription"
            :rules="rules.required"
            :label="`${ $t('Description') }`"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs6
          md2>
          <v-select
            v-model="tank.productFamily"
            :items="options.productTypes"
            :label="`${ $t('Product family') }`"
            :disabled="features.weightsMeasuresLock"
            :rules="[ productValidation ]"
            item-text="desc"
            item-value="code"
            class="dover-input"
            chips
            @change="onProductChange()">
            <template
              slot="selection"
              slot-scope="data">
              <v-chip
                :selected="data.selected"
                :disabled="data.disabled"
                :key="data.item.code"
                class="v-chip--select-multi "
                @input="data.parent.selectItem(data.item)">
                <v-avatar class="dover-avatar ">{{ data.item.code }}</v-avatar>
                {{ data.item.desc }}
              </v-chip>
            </template>
          </v-select>
        </v-flex>
        <v-flex
          xs6
          md4
          text-xs-right>
          <v-btn-toggle
            v-model="tank.autoCalibrationEnabled"
            mandatory
            @change="onTankModeChange()">
            <v-btn
              v-for="(item) in tankModes"
              :key="item.index"
              :value="item.value"
              text
              small
              color="dover-blue">
              {{ item.desc }}
            </v-btn>
          </v-btn-toggle>
        </v-flex>
        <v-flex
          v-if="!Maglink.isPlus"
          xs6
          md3>
          <v-text-field
            v-model="tank.serial"
            :counter="10"
            :disabled="features.weightsMeasuresLock"
            :rules="[ probeSerialValidation ]"
            :label="`${ $t('Serial Number') }`"
            mask="##########"
            class="dover-input"
            @input="onSerialChange()"/>
        </v-flex>
        <v-flex
          v-if="Maglink.isPlus"
          xs6
          md3>
          <v-combobox
            v-model="tank.serial"
            :counter="10"
            :disabled="features.weightsMeasuresLock"
            :items="probeDetectedSerials"
            :label="`${ $t('Serial Number') }`"
            :rules="[ probeSerialValidation ]"
            mask="##########"
            class="dover-input"
            dense
            @input="onSerialChange()">
            <template
              slot="selection"
              slot-scope="data">
              <v-chip
                :selected="data.selected"
                class="v-chip--select-multi "
                @input="data.parent.selectItem(data.item)">
                <strong>{{ data.item }}</strong>
              </v-chip>
            </template>
          </v-combobox>
        </v-flex>
        <v-flex
          xs6
          md9
          text-xs-left>
          <v-btn
            v-if="isQueryProbe"
            :disabled="progressBar.loading"
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="queryProbe">
            {{ $t('Query Probe') }}
          </v-btn>
        </v-flex>
        <v-flex
          v-if="progressBar.loading"
          xs6>
          <span>{{ progressBar.label }}</span>
          <MaglinkComponentLoadingBar
            :loading="progressBar.loading"
            :indeterminate="false"
            :max-value="progressBar.maxValue"
            :value="progressBar.currentValue" />
        </v-flex>
        <v-flex
          v-if="progressBar.loading"
          xs6/>
        <v-layout
          style="padding-left: 12px;margin-left: -0px;"
          wrap>
          <v-row>
            <v-col
              md12>
              <v-select
                v-model="tank.probeType"
                :disabled="features.weightsMeasuresLock || isProbeRecognized"
                :items="probeTypes"
                :label="`${ $t('Probe type') }`"
                item-text="desc"
                item-value="code"
                class="dover-input"
                chips
                @change="onProbeTypeUserChange()">
                <template
                  slot="selection"
                  slot-scope="data">
                  <v-chip
                    :selected="data.selected"
                    :disabled="data.disabled"
                    :key="data.item.code"
                    class="v-chip--select-multi "
                    @input="data.parent.selectItem(data.item)">
                    {{ data.item.desc }}
                  </v-chip>
                </template>
              </v-select>
              <v-select
                v-if="isProbeRadar"
                v-model="tank.applicationType"
                :disabled="features.weightsMeasuresLock"
                :items="radarApplicationTypes"
                :label="`${ $t('Application type') }`"
                item-text="desc"
                item-value="code"
                class="dover-input text-xs"
                dense
                @input="onRadarApplicationChange"/>
              <v-text-field
                v-if="isProbeAnalog"
                v-model="tank.probeSerialLabel"
                :counter="40"
                :label="`${ $t('Serial label') }`"
                class="dover-input"/>
              <v-text-field
                v-if="isProbeAnalog"
                v-model="tank.probeModelLabel"
                :counter="17"
                :rules="rules.required"
                :label="`${ $t('Model label') }`"
                :disabled="features.weightsMeasuresLock"
                class="dover-input"/>
              <v-text-field
                v-if="isProbeAnalog"
                v-model.number="probeVelocity"
                :counter="8"
                :rules="rules.required.concat(rules.velocity)"
                :label="`${ $t('Velocity') + ' (' + $t('µs/in') + ')'}`"
                :disabled="features.weightsMeasuresLock"
                class="dover-input"/>
              <v-select
                v-model="tank.probeDensmeter"
                :disabled="features.weightsMeasuresLock || !isDensAllowed"
                :items="densityFloatTypes"
                :label="`${ $t('Density') }`"
                item-text="desc"
                item-value="code"
                class="dover-input text-xs"
                dense
                @input="onDensityFloatChange"/>
              <v-select
                v-model="tank.probeWaterFloat"
                :disabled="features.weightsMeasuresLock || !isWaterAllowed"
                :items="waterFloatTypes"
                :label="`${ $t('Water detection type') }`"
                item-text="desc"
                item-value="code"
                class="dover-input text-xs"
                dense
                @input="onWaterFloatChange"/>
              <v-select
                v-model="tank.probeWaterIgnore"
                :disabled="features.weightsMeasuresLock || (!isWaterAllowed && !isProbeAnalog) || tank.probeWaterFloat === 0"
                :items="waterFloatIgnoreTypes"
                :label="`${ $t('Water') }`"
                item-text="desc"
                item-value="code"
                class="dover-input text-xs"
                dense
                @input="onWaterFloatChange"/>
              <v-text-field
                v-model.number="tank.offsetProductLevel"
                :counter="6"
                :rules="rules.required.concat(rules.level)"
                :label="`${ $t('Offset product') + ' (' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
                :disabled="features.weightsMeasuresLock"
                class="dover-input"/>
              <v-text-field
                v-model.number="tank.offsetWaterLevel"
                :counter="6"
                :rules="rules.required.concat(rules.level)"
                :label="`${ $t('Offset water') + ' (' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
                :disabled="features.weightsMeasuresLock || !isWaterFloat || (!isWaterAllowed && !isProbeAnalog)"
                class="dover-input"/>
              <v-text-field
                v-model.number="tank.waterMinFloatLevel"
                :counter="6"
                :rules="rules.required.concat(rules.levelPositive)"
                :label="`${ $t('Water min float level') + ' (' + [ options.levelUnits[systemSettings.levelUnit || 0].desc ] + ')' }`"
                :disabled="features.weightsMeasuresLock || !isWaterFloat || (!isWaterAllowed && !isProbeAnalog)"
                class="dover-input"/>
            </v-col>
          </v-row>
        </v-layout>
        <v-layout
          column
          fill-high
          align="right">
          <v-img
            :src="probeImage"
            contain
            max-width="240"/>
          <v-img
            :src="probeImageBottom"
            contain
            style="margin-top:-115px"
            max-width="240"/>
          <v-img
            :src="probeImageMid"
            contain
            style="margin-top:-200px"
            max-width="240"/>
        </v-layout>
        <v-flex
          xs12
          md12
          lg12>
          <span
            class="caption grey--text">
            {{ $t('Delivery') }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          v-if="!isTankAcr()"
          xs4
          md4>
          <v-switch
            v-model="siteInfo.fastDeliveryAlgorithm"
            :label="`${ $t('Fast delivery QACR (all tanks)') }`"
            class="dover-input text-xs-center"
            @change="onDeliveryAlgorithmChange()"/>
        </v-flex>
        <v-flex
          xs4
          md4>
          <v-text-field
            v-model.number="tank.stableDeliveryTime"
            :counter="3"
            :rules="rules.required.concat(rules.positive)"
            :label="`${ $t('Delivery time ({0})', [ $t('min') ]) }`"
            mask="###"
            class="dover-input"/>
        </v-flex>
        <v-flex
          v-if="!isTankAcr()"
          xs4
          md4>
          <v-text-field
            v-model="tank.deliveryStarting"
            :counter="5"
            :rules="rules.required.concat(rules.positive)"
            :label="`${ $t('Start delivery detection') + ' (' + [ options.volumeUnits[systemSettings.volumeUnit || 0].desc ] + ')' }`"
            mask="#####"
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs12
          md12
          lg12>
          <span
            class="caption grey--text font-weight-light">
            {{ $t('Tank group') }}
          </span>
          <hr>
        </v-flex>
        <v-flex
          xs6
          md6>
          <v-text-field
            v-model.trim="memberOf"
            :label="memberOfLabel"
            disabled
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs6
          md6>
          <v-text-field
            v-model="tankListString"
            :label="`${ $t('All members') }`"
            disabled
            class="dover-input"/>
        </v-flex>
        <v-flex
          xs12
          md12
          text-xs-right>
          <hr>
          <v-btn
            :disabled="!isValidationOk"
            small
            class="mx-0 font-weight-light"
            color="dover-blue"
            @click="saveTank">
            {{ $t('Save') }}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
import Vue from 'vue'
import { i18n } from '@/i18n'

export default {
  props: {
    tank: {
      type: Object,
      required: true,
      default: () => {}
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    },
    siteInfo: {
      type: Object,
      required: true,
      default: () => {}
    },
    tankGroups: {
      type: Array,
      required: true,
      default: () => []
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    totalTanks: {
      type: Number,
      required: true,
      default: () => 0
    },
    probeSerials: {
      type: Array,
      required: true,
      default: () => []
    },
    features: {
      type: Object,
      required: true,
      default: () => {}
    },
    probeDetected: {
      type: Array,
      required: true,
      default: () => []
    },
    probeDetectedSerials: {
      type: Array,
      required: true,
      default: () => []
    }
  },
  data: () => ({
    isValidationOk: false,
    probeTypes: [],
    waterFloatTypes: [],
    densityFloatTypes: [],
    waterFloatIgnoreTypes: [],
    isProbeAnalog: false,
    isProbeRadar: false,
    groupMembers: undefined,
    tankListString: undefined,
    memberOf: '',
    memberOfLabel: '',
    loadComplete: false,
    isAcr: false,
    tankModes: [
      { index: 0, value: false, desc: i18n.t('Standard mode') },
      { index: 1, value: true, desc: i18n.t('ACR mode') }
    ],
    radarApplicationTypes: [],
    isWaterFloat: true,
    probeImage: '',
    probeImageMid: '',
    probeImageBottom: '',
    rules: {
      required: [ v => Vue.prototype.Maglink.RulesRequired(v) ],
      uniqueSerial: [ v => true ],
      positive: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 99999) ],
      level: [ v => Vue.prototype.Maglink.RulesRange(v, -5000, 5000) ],
      levelPositive: [ v => Vue.prototype.Maglink.RulesRange(v, 0, 5000) ],
      velocity: [ v => Vue.prototype.Maglink.RulesRange(v, 1.0, 20.0) ],
      sequential: true
    },
    isQueryProbe: false,
    isProbeRecognized: false,
    isDensAllowed: false,
    isWaterAllowed: false,
    isDensAefAllowed: true,
    responseTimerId: 0,
    responseTimeCount: 0,
    refreshTimerId: 0,
    progressBar: {
      loading: false,
      maxValue: 100,
      currentValue: 0,
      tickId: 0,
      label: ''
    }
  }),
  computed: {
    probeVelocity: {
      get: function () {
        if (this.tank.probeVelocity > 0.0) {
          return 25400000 / this.tank.probeVelocity // mm/s -> us/in
        }
        return 0.0
      },
      set: function (value) {
        if (value > 0.0) {
          this.tank.probeVelocity = 25400000 / value // us/in -> mm/s
        }
      }
    }
  },
  mounted () {
    // array list with tank list for each group
    this.groupMembers = []
    let tankNumber = this.tank.tankNumber

    this.waterFloatTypes.push({ code: 0, desc: this.$t('Not installed') })
    this.waterFloatTypes.push({ code: 1, desc: this.$t('Water float') })
    this.waterFloatTypes.push({ code: 2, desc: this.$t('AEF float') })

    this.densityFloatTypes.push({ code: 0, desc: this.$t('Not installed') })
    this.densityFloatTypes.push({ code: 1, desc: this.$t('Densimeter') })

    this.waterFloatIgnoreTypes.push({ code: 0, desc: this.$t('Enabled') })
    this.waterFloatIgnoreTypes.push({ code: 2, desc: this.$t('Ignored') })
    this.waterFloatIgnoreTypes.push({ code: 1, desc: this.$t('Ignored during delivery') })

    this.setProbeTypes()

    for (let index = 0; index < this.tankGroups.length; index++) {
      let group = this.tankGroups[index]

      if (group.connectedTanks.length === 0) {
        this.groupMembers.push('')
      } else {
        let desc = group.connectedTanks.join(', ')

        this.groupMembers.push(desc)

        // look for the associated group
        let found = group.connectedTanks.find(element => element === tankNumber) || -1
        if (found !== -1) {
          this.tankListString = desc
          this.memberOfLabel = this.$t('Member of group {0}', [group.groupNumber])
          this.memberOf = group.groupDescription
          break
        }
      }
    }

    this.options.productTypes.sort((a, b) => (a.desc > b.desc) ? 1 : -1)
    this.isAcr = this.tank.autoCalibrationEnabled
    this.isWaterFloat = this.tank.probeWaterFloat !== 0 && this.tank.probeWaterFloat !== 2 // not AEF

    this.setRadarApplicationTypes()
    this.onProbeTypeChange()

    this.startProgressBarTimer()
    this.startResponseTimer(200)
    this.startRefreshTimer()
  },
  imageWidth () {
    return 120
  },
  beforeDestroy () {
    this.stopProgressBarTimer()
    this.stopRefreshTimer()
  },
  methods: {
    stopProgressBarTimer () {
      clearInterval(this.progressBar.tickId)
      this.progressBar.tickId = 0
      this.progressBar.loading = false
      this.progressBar.currentValue = 0
      this.progressBar.label = ''
    },
    startProgressBarTimer () {
      // manage progressbar to show user to wait
      if (this.progressBar.tickId === 0) {
        this.progressBar.label = this.$t('Awaiting for probe response...')
        this.progressBar.loading = true
        this.progressBar.currentValue = 0
        this.progressBar.tickId = setInterval(this.progressBarTick, 100)
      }
    },
    progressBarTick () {
      if (this.progressBar.currentValue >= this.progressBar.maxValue) {
        this.progressBar.currentValue = 0
      }
      this.progressBar.currentValue += 1
    },
    startResponseTimer (msecs) {
      this.responseTimerId = setTimeout(this.responseCommandTimerTick, msecs)
      this.responseTimeCount = 0
    },
    responseCommandTimerTick () {
      if (this.responseTimeCount > 12) {
        this.stopProgressBarTimer()
        this.snack('error', this.$t('No response from the probe'))
        return
      }
      // read response
      this.Maglink.GetAutodetectionStatus()
        .then(response => {
          // response.data.updated
          if (response.data.updated) {
            this.stopProgressBarTimer()
            this.getDetectedDevices()
          } else {
            this.responseTimerId = setTimeout(this.responseCommandTimerTick, 4000)
            this.responseTimeCount += 1
          }
        })
        .catch(error => {
          this.snack('error', error.message)
        })
    },
    startRefreshTimer (msecs) {
      if (msecs === undefined) {
        msecs = 10000
      }

      this.refreshTimerId = setTimeout(this.refreshTimerTick, msecs)
    },
    stopRefreshTimer () {
      clearInterval(this.refreshTimerId)
      this.refreshTimerId = 0
    },
    refreshTimerTick () {
      this.$emit('measurementRefresh', this.tank.tankNumber)
      this.startRefreshTimer()
    },
    isProbeDetected (number) {
      for (var i = 0; i < this.probeDetected.length; i++) {
        if (this.probeDetected[i].serial * 1 === number * 1) {
          return true
        }
      }
      return false
    },
    setProbeTypes () {
      this.probeTypes = this.Maglink.ProbeTypesBasic()
      if (Vue.prototype.Maglink.isPlus) {
        this.probeTypes = this.probeTypes.concat(this.Maglink.ProbeTypesPlus())
      }
      this.probeTypes.sort((a, b) => (a.index > b.index) ? 1 : -1)
    },
    isAnalog (type) {
      for (var i = 0; i < this.probeTypes.length; i++) {
        if (this.probeTypes[i].code === type) {
          if (this.probeTypes[i].isAnalog) {
            return true
          }
          return false
        }
      }
      return false
    },
    isRadar (type) {
      for (var i = 0; i < this.probeTypes.length; i++) {
        if (this.probeTypes[i].code === type) {
          if (this.probeTypes[i].isRadar) {
            return true
          }
          return false
        }
      }
      return false
    },
    setRadarApplicationTypes () {
      this.radarApplicationTypes = []
      if (this.tank.productFamily === 21) { // solid
        this.radarApplicationTypes.push({ code: 0, desc: this.$t('Silo'), image: './img/tank/probe_radar_silo.png' })
        this.radarApplicationTypes.push({ code: 4, desc: this.$t('Bin/Hopper'), image: './img/tank/probe_radar_bin.png' })
        this.radarApplicationTypes.push({ code: 3, desc: this.$t('Open'), image: './img/tank/probe_radar_open_solid.png' })
      } else {
        this.radarApplicationTypes.push({ code: 0, desc: this.$t('Storage'), image: './img/tank/probe_radar_storage.png' })
        this.radarApplicationTypes.push({ code: 1, desc: this.$t('Process'), image: './img/tank/probe_radar_process.png' })
        this.radarApplicationTypes.push({ code: 13, desc: this.$t('Wetwell'), image: './img/tank/probe_radar_wetwell.png' })
        this.radarApplicationTypes.push({ code: 8, desc: this.$t('Plastic'), image: './img/tank/probe_radar_plastic.png' })
        this.radarApplicationTypes.push({ code: 9, desc: this.$t('Open'), image: './img/tank/probe_radar_open.png' })
      }
    },
    setProbeImage () {
      var isImage = false
      for (var i = 0; i < this.probeTypes.length; i++) {
        if (this.probeTypes[i].code === this.tank.probeType) {
          if (this.isRadar(this.tank.probeType)) {
            this.setRadarImage()
          } else if (this.isAnalog(this.tank.probeType)) {
            this.setAnalogImage()
          } else {
            this.probeImage = this.probeTypes[i].image
          }
          isImage = true
          break
        }
      }
      if (isImage) {
        this.setProbeDensityFloatImage()
        this.setProbeWaterFloatImage()
      } else {
        this.probeImage = './img/tank/probe_empty.png'
        this.probeImageMid = './img/tank/probe_dens_empty.png'
        this.probeImageBottom = './img/tank/probe_water_empty.png'
      }
    },
    setRadarImage () {
      for (var i = 0; i < this.radarApplicationTypes.length; i++) {
        if (this.tank.applicationType === this.radarApplicationTypes[i].code) {
          this.probeImage = this.radarApplicationTypes[i].image
          return
        }
      }
      this.probeImage = './img/tank/probe_radar.png'
    },
    setAnalogImage () {
      var detectedAnalogType = 0
      var i = 0
      for (i = 0; i < this.probeDetected.length; i++) {
        if (this.probeDetected[i].serial * 1 === this.tank.serial * 1) {
          detectedAnalogType = this.probeDetected[i].probeType
          break
        }
      }
      for (i = 0; i < this.probeTypes.length; i++) {
        if (this.probeTypes[i].code === this.tank.probeType && this.probeTypes[i].isAnalog && this.probeTypes[i].analogType === detectedAnalogType) {
          this.probeImage = this.probeTypes[i].image
          return
        }
      }
      this.probeImage = './img/tank/probe_ametek.png'
    },
    setProbeDensityFloatImage () {
      if (this.tank.probeDensmeter === 0) {
        this.probeImageMid = './img/tank/probe_dens_empty.png'
        return
      }
      for (var i = 0; i < this.probeTypes.length; i++) {
        if (this.probeTypes[i].code === this.tank.probeType) {
          this.probeImageMid = this.probeTypes[i].imageDensityFloat
          break
        }
      }
    },
    setProbeWaterFloatImage (type) {
      if (this.tank.probeWaterFloat === 0) {
        this.probeImageBottom = './img/tank/probe_water_empty.png'
        return
      }
      for (var i = 0; i < this.probeTypes.length; i++) {
        if (this.probeTypes[i].code === this.tank.probeType) {
          if (this.tank.probeWaterFloat === 1) { // water float
            this.probeImageBottom = this.probeTypes[i].imageWaterFloat
          } else { // AEF float
            this.probeImageBottom = this.probeTypes[i].imageAefFloat
          }
          break
        }
      }
    },
    enableFloats () {
      for (var i = 0; i < this.probeTypes.length; i++) {
        if (this.probeTypes[i].code === this.tank.probeType) {
          this.isDensAllowed = this.probeTypes[i].isDensAllowed
          this.isWaterAllowed = this.probeTypes[i].isWaterAllowed
          this.isDensAefAllowed = this.probeTypes[i].isDensAefAllowed
          if (!this.isDensAllowed) {
            this.tank.probeDensmeter = 0
          }
          if (!this.isWaterAllowed) {
            if (this.isProbeAnalog) {
              this.tank.probeWaterFloat = 1
              this.tank.probeWaterIgnore = 0
            } else {
              this.tank.probeWaterFloat = 0
              this.tank.probeWaterIgnore = 2
            }
          }
          return
        }
      }
      this.isDensAllowed = false
      this.isWaterAllowed = false
      this.tank.probeDensmeter = 0
      this.tank.probeWaterFloat = 0
      this.tank.probeWaterIgnore = 2
    },
    probeSerialValidation () {
      let t = this.tank
      let value = t.serial * 1 // convert to number

      // required check
      this.isValidationOk = true
      let result = Vue.prototype.Maglink.RulesRequired(t.serial)
      let isRequired = typeof result === 'string'
      if (isRequired) {
        this.isValidationOk = false
        return result
      }

      for (let index = 0; index < this.probeSerials.length; index++) {
        let item = this.probeSerials[index]
        if (item.tank === t.tankNumber) {
          continue
        }
        if (item.serial * 1 === value) {
          result = this.$t('Probe serial "{0}" already used in tank "{1}"', [ t.serial, item.tank ])
          this.isValidationOk = false
          break
        }
      }

      return result
    },
    productValidation () {
      if (this.tank.productFamily === 21 && !this.isProbeRadar) {
        this.isValidationOk = false
        return this.$t('Invalid product')
      }
      return true
    },
    setDefaultWater () {
      this.tank.probeWaterFloat = 1
      this.tank.probeWaterIgnore = 0
    },
    onSerialChange () {
      this.setDefaultWater()
      if (this.tank.serial.length > 0 && this.tank.serial.length < 6 && !this.isProbeDetected(this.tank.serial)) {
        this.isQueryProbe = true
      } else {
        this.isQueryProbe = false
        this.setRecognizedProbe()
      }
      this.onProbeTypeChange()
    },
    onProbeTypeUserChange () {
      this.setDefaultWater()
      this.onProbeTypeChange()
    },
    onProbeTypeChange () {
      this.isProbeAnalog = this.isAnalog(this.tank.probeType)
      this.isProbeRadar = this.isRadar(this.tank.probeType)
      this.isProbeRecognized = false // not used -> this.tank.probeType !== 0 && !this.isQueryProbe
      if (this.tank.probeDensmeter === null) {
        this.tank.probeDensmeter = 0
      }
      this.enableFloats()
      this.setProbeImage()
    },
    onRadarApplicationChange () {
      this.setRadarImage()
    },
    saveTank (event) {
      if (this.$refs.form.validate()) {
        this.isValidationOk = true
        this.tank.serial = this.Maglink.PadRight(this.tank.serial, 10)
        this.tank.stableDeliveryTime = this.tank.stableDeliveryTime * 1
        this.tank.deliveryStarting = this.tank.deliveryStarting * 1
        this.$emit('tankSaved', this.tank.tankNumber)
      } else {
        this.isValidationOk = false
      }
    },
    onTankModeChange () {
      this.isAcr = this.tank.autoCalibrationEnabled
      this.$emit('tankModeChange', this.tank.tankNumber, this.tank.autoCalibrationEnabled)
    },
    onDeliveryAlgorithmChange () {
      this.$emit('deliveryAlgorithmChange', this.siteInfo.fastDeliveryAlgorithm)
    },
    isTankAcr () {
      return this.isAcr
    },
    isTankFastDelivery () {
      return this.siteInfo.fastDeliveryAlgorithm
    },
    onWaterFloatChange () {
      this.isWaterFloat = this.tank.probeWaterFloat !== 0 && this.tank.probeWaterFloat !== 2 // not AEF
      if (!this.isWaterFloat) {
        this.tank.waterMinFloatLevel = 0
        this.tank.offsetWaterLevel = 0
      }
      if (this.tank.probeWaterFloat === 2 && !this.isDensAefAllowed) { // AEF
        this.tank.probeDensmeter = 0
      }
      if (this.tank.probeWaterFloat === 0) {
        this.tank.probeWaterIgnore = 2
      }
      this.onProbeTypeChange()
      this.$emit('floatChanged', this.tank.tankNumber)
    },
    onDensityFloatChange () {
      if (this.tank.probeDensmeter === 1 && !this.isDensAefAllowed) { // densimeter
        this.tank.probeWaterFloat = 1
        this.tank.probeWaterIgnore = 0
      }
      this.onProbeTypeChange()
      this.$emit('floatChanged', this.tank.tankNumber)
    },
    onProductChange () {
      this.setRadarApplicationTypes()
      if (this.isProbeRadar) {
        this.tank.applicationType = 0
        this.setRadarImage()
      }
      this.$emit('productChanged', this.tank.tankNumber, this.tank.productFamily)
    },
    queryProbe: function (event) {
      if (this.tank.serial === '' || this.progressBar.loading) {
        return
      }
      this.Maglink.StartAutodetection('D'.charCodeAt(0), parseInt(this.tank.serial))
        .then(response => {
          this.startProgressBarTimer()
          this.startResponseTimer(2000)
        })
        .catch(error => (this.snack('error', error.message)))
    },
    getDetectedDevices () {
      this.Maglink.GetAutodetections()
        .then(response => {
          var bFound = false
          this.probeDetected = []
          this.probeDetectedSerials = []
          response.data.forEach(item => {
            if (item.device === 80) {
              var text = this.Maglink.DetectedSerialName(item)
              if (text !== '') {
                this.probeDetected.push(item)
                this.probeDetectedSerials.push(text)
                if (item.serial * 1 === this.tank.serial * 1) {
                  bFound = true
                }
              }
            }
          })
          if (this.isQueryProbe && bFound) {
            this.snack('success', this.$t('Probe detected'))
          }
          if (this.isQueryProbe && !bFound) {
            this.snack('error', this.$t('Probe not connected'))
          }
          this.setRecognizedProbe()
          this.$emit('probesDetected', this.probeDetected, this.probeDetectedSerials)
        })
        .catch(error => (this.snack('error', error.message)))
    },
    setRecognizedProbe () {
      for (var i = 0; i < this.probeDetected.length; i++) {
        if (this.probeDetected[i].serial * 1 === this.tank.serial * 1) {
          this.tank.probeType = this.probeDetected[i].model
          this.onProbeTypeChange()
        }
      }
    },
    snack (color, message) {
      message = this.$t('Tank settings') + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
<style scoped>
.v-item-group {
    FONT-WEIGHT: 500;
    -webkit-box-flex: 0;
    -ms-flex: 0 1 auto;
    flex: 0 1 auto;
    position: relative;
    -webkit-transition: .3s cubic-bezier(.25,.8,.5,1);
    transition: .3s cubic-bezier(.25,.8,.5,1);
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: horizontal;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: inherit;
    height: auto;
    -webkit-box-flex: 1;
    -ms-flex-positive: 1;
    flex-grow: 1;
    -ms-flex-wrap: wrap;
    flex-wrap: wrap;
    width: 100%;
}
.v-btn-toggle--selected {
    -webkit-box-shadow: 0 0px 0px 0px rgba(0,0,0,0), 0 0px 0px 0 rgba(0,0,0,0), 0 0px 0px 0 rgba(0,0,0,0);
    box-shadow: 0 0px 0px 0px rgba(0,0,0,0), 0 0px 0px 0 rgba(0,0,0,0), 0 0px 0px 0 rgba(0,0,0,0);
}
</style>
